/** Last-email context is separate from immutable acquisition/first-touch data.
 * It is a hint: the checkout server verifies the actual signed-in recipient. */
export type EmailTouch = { token: string; capturedAt: number };
export const EMAIL_TOUCH_KEY = "crimp:last-email-touch:v1";
export const EMAIL_TOUCH_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
type Storage = { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void };

export function validEmailTouch(value: unknown, now = Date.now()): value is EmailTouch {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<EmailTouch>;
  return typeof candidate.token === "string" && /^[a-f0-9]{64}$/.test(candidate.token) &&
    typeof candidate.capturedAt === "number" && Number.isFinite(candidate.capturedAt) &&
    candidate.capturedAt <= now && now - candidate.capturedAt < EMAIL_TOUCH_MAX_AGE;
}

export function readEmailTouch(storage: Storage, now = Date.now()): EmailTouch | null {
  try {
    const value: unknown = JSON.parse(storage.getItem(EMAIL_TOUCH_KEY) ?? "null");
    if (validEmailTouch(value, now)) return value;
    storage.removeItem(EMAIL_TOUCH_KEY);
  } catch { /* Storage is optional; never block page entry or checkout. */ }
  return null;
}

export function storeEmailTouch(storage: Storage, value: EmailTouch | null): void {
  try {
    if (value) storage.setItem(EMAIL_TOUCH_KEY, JSON.stringify(value));
    else storage.removeItem(EMAIL_TOUCH_KEY);
  } catch { /* Private browsing may disable persistence. */ }
}

/** Delete every token value, including malformed ones, before analytics sees
 * the URL. UTMs stay campaign-only; no customer identity is inferred here. */
export function emailArrival(href: string, previous: EmailTouch | null, now = Date.now()): { url: URL; touch: EmailTouch | null; hadToken: boolean } {
  const url = new URL(href);
  const token = url.searchParams.get("em");
  const hadToken = url.searchParams.has("em");
  url.searchParams.delete("em");
  const retained = validEmailTouch(previous, now) ? previous : null;
  const touch = token && /^[a-f0-9]{64}$/.test(token)
    ? retained?.token === token ? retained : { token, capturedAt: now }
    : retained;
  return { url, touch, hadToken };
}

/** Defence in depth for automatic capture before a navigation is processed,
 * including percent-encoded hrefs and links pasted into other application UI. */
export function redactEmailTrackingUrls(value: string): string {
  return value
    .replace(/((?:\/|%2f)e(?:\/|%2f)[cu](?:\/|%2f))[a-f0-9]{64}/gi, "$1[redacted]")
    .replace(/((?:[?&]|%3f|%26)em(?:=|%3d))[^&#]*?(?=[&#]|%26|%23|$)/gi, "$1[redacted]");
}
